import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FETCH_MODEL_PORTFOLIOS } from '../queries';
import {
  mapModelPortfolioSearchHits,
  pinSelectedModelOptions,
} from '../utils/modelSearchOptions';
import type { ModelOption } from '../utils/portfolioFilters';

export const MODEL_FILTER_OPTIONS_PAGE_SIZE = 500;

export function useModelPortfolioFilterOptions({
  organizationId,
  selectedModelIds,
  selectedModelNames,
}: {
  organizationId?: string;
  selectedModelIds: string[];
  selectedModelNames: Record<string, string>;
}) {
  const { data, loading } = useQuery(FETCH_MODEL_PORTFOLIOS, {
    skip: !organizationId,
    variables: {
      input: {
        filter: {
          organizationId,
        },
        pagination: { perPage: MODEL_FILTER_OPTIONS_PAGE_SIZE },
      },
    },
  });

  const options = useMemo(
    () =>
      pinSelectedModelOptions(
        mapModelPortfolioSearchHits(
          data?.fetchModelPortfolios?.modelPortfolios,
        ),
        selectedModelIds,
        selectedModelNames,
      ),
    [data, selectedModelIds, selectedModelNames],
  );

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const pendingCatalogRef = useRef<(() => void)[]>([]);

  useEffect(() => {
    if (loading) return;
    const pending = pendingCatalogRef.current;
    pendingCatalogRef.current = [];
    for (const resolveCatalog of pending) resolveCatalog();
  }, [loading]);

  const onSearchModels = useCallback(
    async (query: string): Promise<ModelOption[]> => {
      if (loadingRef.current) {
        await new Promise<void>((resolveCatalog) => {
          pendingCatalogRef.current.push(resolveCatalog);
        });
      }
      const normalized = query.trim().toLowerCase();
      const current = optionsRef.current;
      if (!normalized) return current;
      return current.filter((option) =>
        option.name.toLowerCase().includes(normalized),
      );
    },
    [],
  );

  return { options, loading, onSearchModels };
}
